import type { CompetitorFilters } from "../types/dashboard.types";
import type { DashboardKpiCardsState } from "./card-state";
import { isCompetitorFiltersComplete } from "../types/dashboard.types";

/** What the header's seeding effects are about to work with, read on the same render. */
export interface CompetitorSeedInputs {
  draft: CompetitorFilters;
  isIndustriesSettled: boolean;
  isDefaultsSettled: boolean;
  /** Roster query for `draft.myIndustry` succeeded or failed. */
  isMyIndustryBrandsSettled: boolean;
  /** Roster query for `draft.competitorsIndustry` succeeded or failed. */
  isCompetitorBrandsSettled: boolean;
  /** `draft.myBrand`, or the brand seeding would fill in. */
  projectedMyBrand: string | null;
  /** `draft.mainCompetitor`, or the competitor seeding would fill in. */
  projectedMainCompetitor: string | null;
}

/**
 * True once seeding has nothing left to wait on and still cannot complete the
 * filters — e.g. an industry whose competitor roster is empty. Uses projected
 * values rather than the draft, so the render where a roster lands does not read
 * as incomplete before the seeding effect writes it.
 */
export function isCompetitorSeedSettledIncomplete(inputs: CompetitorSeedInputs): boolean {
  if (!inputs.isIndustriesSettled || !inputs.isDefaultsSettled) return false;
  if (!inputs.draft.myIndustry || !inputs.draft.competitorsIndustry) return false;
  if (!inputs.isMyIndustryBrandsSettled || !inputs.isCompetitorBrandsSettled) return false;
  return !inputs.projectedMyBrand || !inputs.projectedMainCompetitor;
}

/**
 * `complete` — request and show data.
 * `seeding` — the header is still filling the filters in (e.g. right after a tenant
 *   switch): send nothing and show skeletons, so no message flashes before the data.
 * `blocked` — seeding settled without a brand or competitor: send nothing and ask
 *   the user to pick one.
 */
export type CompetitorSelectionState = "complete" | "seeding" | "blocked";

export function resolveCompetitorSelectionState(
  appliedFilters: CompetitorFilters,
  isSeedSettledIncomplete: boolean,
): CompetitorSelectionState {
  if (isCompetitorFiltersComplete(appliedFilters)) return "complete";
  return isSeedSettledIncomplete ? "blocked" : "seeding";
}

/** UI state for a competitor section: its resource's state only once the selection is complete. */
export function toCompetitorSectionState(
  selection: CompetitorSelectionState,
  resourceState: DashboardKpiCardsState,
): DashboardKpiCardsState | "blocked" {
  if (selection === "blocked") return "blocked";
  if (selection === "seeding") return "loading";
  return resourceState;
}
